import { API } from 'lib/api';
import { MutationFunction, QueryFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { BILLS_ENDPOINTS } from 'constant/endpoints/bill';
import { getSanitizedPagination } from 'utils/table/pagination';
import {
  IBillAuditLogResponse,
  IBillScanAuditLogsQueryParams,
  IDismissBillAuditLog,
} from './types';
import {
  IBillsSummaryRequestParams,
  IBillsSummaryResponse,
} from '../types/billSettings';
import {
  IBillDataReviewedParams,
  IBillDataReviewedResponse,
  IBillsListParams,
  IBillsListResponse,
  IBillsUserPersistPostBody,
  IBillsUserPersistResponse,
} from '../types';

export const fetchBillScanAuditLogs: QueryFunction<
  IBillAuditLogResponse,
  readonly [string, IBillScanAuditLogsQueryParams],
  number
> = async ({ signal, queryKey, pageParam }) => {
  const { per_page } = queryKey[1];
  const { page: sanitizedPage, per_page: sanitizedPerPage } =
    getSanitizedPagination({
      page: Number(pageParam),
      per_page,
    });

  const { data } = await API.get<IBillAuditLogResponse>(
    BILLS_ENDPOINTS.GET_SCANNED_BILL_AUDIT_LOGS,
    {
      signal,
      params: {
        page: sanitizedPage,
        per_page: sanitizedPerPage,
      },
    },
  );
  return data;
};

export const dismissBillAuditLog: MutationFunction<
  AxiosResponse,
  IDismissBillAuditLog
> = async (data) =>
  API.put(BILLS_ENDPOINTS.DISMISS_SCANNED_BILL_AUDIT_LOG, { ...data });

export const fetchBillsSummary = async (
  payload: IBillsSummaryRequestParams,
): Promise<IBillsSummaryResponse> => {
  const { data } = await API.post(BILLS_ENDPOINTS.FETCH_SUMMARY, {
    ...payload,
  });
  return data;
};

export const fetchBillsList = async (
  payload: Partial<IBillsListParams>,
  signal?: AbortSignal,
): Promise<IBillsListResponse> => {
  const { data } = await API.post<IBillsListResponse>(
    BILLS_ENDPOINTS.GET_BILLS,
    payload,
    {
      signal,
    },
  );

  return data;
};

export const fetchBillsUserPersist = async (
  signal?: AbortSignal,
): Promise<IBillsUserPersistResponse> => {
  const { data } = await API.get<IBillsUserPersistResponse>(
    BILLS_ENDPOINTS.BILLS_USER_PERSIST,
    {
      signal,
    },
  );

  return data;
};

export const persistBillsFilter = async (
  payload: IBillsUserPersistPostBody,
): Promise<IBillsUserPersistResponse> => {
  const { data } = await API.post<IBillsUserPersistResponse>(
    BILLS_ENDPOINTS.BILLS_USER_PERSIST,
    payload,
  );

  return data;
};

export const billDataReviewed = async (
  params: IBillDataReviewedParams,
): Promise<IBillDataReviewedResponse> => {
  const { data } = await API.put<IBillDataReviewedResponse>(
    BILLS_ENDPOINTS.BILL_DATA_REVIEWED,
    {},
    {
      params,
    },
  );

  return data;
};
